import { z } from "zod";

/** Money is micro-dollars in bigint (ADR 0002). 1 USD = 1_000_000 micro. */
export type Micro = bigint;
export const MICRO_PER_USD = 1_000_000n;

/** Parse a decimal USD string ("0.0003", "10", "-1.25") exactly, no floats. */
export function usdToMicro(usd: string): Micro {
  const m = /^(-)?(\d+)(?:\.(\d{1,6}))?$/.exec(usd.trim());
  if (!m) throw new RangeError(`not a USD amount with at most 6 decimals: ${usd}`);
  const [, sign, whole, frac = ""] = m;
  const micro = BigInt(whole!) * MICRO_PER_USD + BigInt(frac.padEnd(6, "0"));
  return sign ? -micro : micro;
}

/** Format micro-dollars as a USD string with `decimals` places (2 by default, 4 when a cent would read as zero). */
export function microToUsd(micro: Micro, decimals: 2 | 4 | 6 = 2): string {
  const neg = micro < 0n;
  const abs = neg ? -micro : micro;
  const divisor = 10n ** BigInt(6 - decimals);
  const rounded = (abs + divisor / 2n) / divisor; // round half up at the display precision
  const scale = 10n ** BigInt(decimals);
  const whole = rounded / scale;
  const frac = (rounded % scale).toString().padStart(decimals, "0");
  return `${neg ? "-" : ""}${whole}.${frac}`;
}

/**
 * The margin on a customer's call, in basis points: 20%. `MARGIN_BPS` in `@joogoos/config` has the same default, and no
 * box sets its own, so this is the one place the number lives: the plan and the picker are drawn in the browser, which
 * cannot read a box's setting, and a box with its own would charge what neither of them says.
 *
 * 2026-10-02, the owner: 20%, down from 40%, on the condition that Joogoos never loses money on it. The floor is the
 * card fee, because the margin is all there is to pay it from: Stripe takes 2.9% + 30¢ of a US card and 1.5% more of a
 * foreign one, which is f = 7.4% of a $10 pack on a UK card, and spending all of it at cost needs m ≥ f / (1 − f), about
 * 8%. The leaks the 40% was quietly absorbing were closed with it: pictures and the plan priced under the vendor's bill,
 * failed calls the vendor billed charged nothing, and Claude's fallback attempts priced as one. What 20 points still
 * carries that nothing charges — tax on the vendors' bills, VAT on sales, refunds and disputes — is in docs/04.
 */
export const DEFAULT_MARGIN_BPS = 2000;

/** charged = ceil(cost × (1 + bps/10000)), integer math only. */
export function applyMargin(cost: Micro, marginBps: number): Micro {
  if (!Number.isInteger(marginBps) || marginBps < 0) throw new RangeError("marginBps must be a non-negative integer");
  const num = cost * BigInt(10_000 + marginBps);
  const den = 10_000n;
  return num % den === 0n ? num / den : num / den + 1n;
}

/** On the wire money is a decimal string of micro-dollars; in code it is bigint. */
export const MicroSchema = z.union([
  z.bigint(),
  z.string().regex(/^-?\d+$/).transform((s) => BigInt(s)),
]);

/* ── buying it, and the ceilings on spending it ─────────────────────────────────────────────────────────────── */

/**
 * What credit can be bought in, in whole dollars
 * ([ADR 0012](../../../docs/adr/0012-pay-as-you-go-with-a-monthly-cap.md), [ADR 0021](../../../docs/adr/0021-credit-belongs-to-the-person.md)).
 *
 * Three packs and no custom amount, because the smallest one has to clear the card fee. The Stripe account is JOOGOOS
 * LLC's, a US one: 2.9% + 30¢ of a US card and 1.5% more of a foreign one, so a $5 top-up on a UK card loses 52¢, a
 * tenth of it, before anything is made, and $10 loses 74¢. Ten dollars is where that stops being embarrassing, and it
 * is the pack the margin's floor is worked out on (`DEFAULT_MARGIN_BPS`). Until 2026-10-02 this quoted a UK account's
 * 20p plus 1.5%, which is not the account Joogoos has.
 *
 * Here rather than in the web app because the API checks what comes back against the same list: a price that arrives
 * from a browser is a number a browser chose.
 */
export const TOP_UPS = [10, 25, 50] as const;
export type TopUp = (typeof TOP_UPS)[number];
export const isTopUp = (dollars: number): dollars is TopUp => (TOP_UPS as readonly number[]).includes(dollars);

/**
 * The lowest and highest a person may set either of their own ceilings to — the monthly cap they choose
 * (guard 4, ADR 0012) and the daily one they can now change (guard 3, [13](../../../docs/13-cost-guards.md)).
 *
 * A dollar at the bottom, from ADR 0012: a cap you cannot set low is not a cap you would trust. A hundred at the top
 * because the box's global daily cap ($200 in production) counts everybody: a limit above half of it would let one
 * customer's day stop every other customer's calls (2026-10-31).
 */
export const CAP_MIN_USD = 1;
export const CAP_MAX_USD = 100;

/**
 * What one grant from the desk may be worth ([ADR 0048](../../../docs/adr/0048-credit-is-bought-before-it-is-spent.md)).
 *
 * Its own pair rather than the ceilings' above, because they answer different questions — a ceiling is what a
 * customer may let themselves spend, and this is what an operator may hand out in one press. A thousand is a
 * deliberate mistake-bound and not a policy: nothing stops a second grant, so the cost of the limit being too low is
 * one more press, and the cost of it being absent is a typed zero too many on somebody's account.
 */
export const GRANT_MIN_USD = 1;
export const GRANT_MAX_USD = 1000;
